import {AbstractControl, ValidationErrors} from '@angular/forms';
import {Subscription} from 'rxjs';

/** Solo espacios exteriores; conserva ceros iniciales y caracteres interiores. */
export const normalizeDocumentNumber = (value: any): any => typeof value === 'string' ? value.trim() : value;

export const isValidRuc = (value: string): boolean => {
    const ruc = normalizeDocumentNumber(value);
    if (typeof ruc !== 'string' || !/^(10|15|16|17|20)\d{9}$/.test(ruc)) {
        return false;
    }
    const pesos = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
    const suma = pesos.reduce((acc, peso, i) => acc + peso * Number(ruc[i]), 0);
    return (11 - (suma % 11)) % 10 === Number(ruc[10]);
};

export const rucValidator = (tipoControlName?: string) => (control: AbstractControl): ValidationErrors | null => {
    if (tipoControlName) {
        const tipo = control.parent?.get(tipoControlName)?.value;
        if (normalizeDocumentNumber(tipo?.codigo ?? tipo) !== '6') {
            return null;
        }
    }
    // La obligatoriedad queda en Validators.required. Un texto de espacios no es un RUC vacío válido.
    if (control.value === null || control.value === undefined || control.value === '') {
        return null;
    }
    return isValidRuc(control.value) ? null : {rucInvalido: true};
};

/** Compras admite otros documentos sin tipo: conservar el comportamiento de DNI y documentos extranjeros. */
export const rucValidatorSiOnceDigitos = () => {
    const validator = rucValidator();
    return (control: AbstractControl): ValidationErrors | null =>
        /^\d{11}$/.test(normalizeDocumentNumber(control.value) ?? '') ? validator(control) : null;
};

export const watchRucValidation = (group: AbstractControl, numeroName: string, tipoName: string): Subscription => {
    const numero = group.get(numeroName);
    numero.addValidators(rucValidator(tipoName));
    numero.updateValueAndValidity({emitEvent: false});
    return group.get(tipoName).valueChanges.subscribe(() => numero.updateValueAndValidity({emitEvent: false}));
};

const documentFields = [
    'ruc', 'rucEmisor', 'rucDocumentoRelacionado', 'tipoDocumento', 'numeroDocumento',
    'tipoDocumentoReceptor', 'numeroDocumentoReceptor', 'numeroDocumentoProveedor',
    ...['Remitente', 'Destinatario', 'Tercero', 'Transportista', 'Conductor'].reduce((fields, sujeto) => fields.concat([
        'tipoDocumentoIdentidad' + sujeto, 'numeroDocumentoIdentidad' + sujeto
    ]), [] as string[])
];

/** Copia solo los objetos de identidad que cambian; no modifica el formulario/modelo ni agrega campos ausentes. */
export const normalizeDocumentPayload = <T>(model: T): T => {
    if (!model || typeof model !== 'object') {
        return model;
    }
    const result: any = {...model};
    documentFields.forEach(field => {
        if (Object.prototype.hasOwnProperty.call(model, field)) {
            result[field] = normalizeDocumentNumber(model[field]);
        }
    });
    ['cliente', 'proveedor'].forEach(field => {
        if (model[field] && typeof model[field] === 'object') {
            result[field] = normalizeDocumentPayload(model[field]);
        }
    });
    return result;
};
